import bcrypt from 'bcryptjs';
import { uploadFileAndGetUrl, deleteFileFromBucket } from '../utils/supabaseUtils';
import { supabase } from '../supabase/supabaseClient';
import { sendNonActiveEmail } from '../email/emailService';

export const checkDuplicateIdCardNumber = async (idNumber) => {
  const { data, error } = await supabase
    .from("members")
    .select("id")
    .eq("id_card_number", idNumber)
    .limit(1);

  return data && data.length > 0;
};

export const getRoles = async () => {
  const { data, error } = await supabase
    .from('roles')
    .select('id, name')
    .order('id', { ascending: true });

  if (error) throw error;
  return data || [];
};

export const registerMember = async (formData) => {
  try {
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!anonKey) {
      throw new Error('Konfigurasi Supabase anonymous key belum tersedia');
    }

    // 1️⃣ Upload foto & KTP (frontend masih boleh)
    let photoUrl = null;
    let id_cardUrl = null;

    if (formData.photo && typeof formData.photo !== "string") {
      photoUrl = await uploadFileAndGetUrl(
        formData.photo,
        formData.full_name,
        "photo",
        "uploads"
      );
    }

    if (formData.id_card && typeof formData.id_card !== "string") {
      id_cardUrl = await uploadFileAndGetUrl(
        formData.id_card,
        formData.full_name,
        "id_card",
        "uploads"
      );
    }

    // 2️⃣ Kirim ke Edge Function
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/register-member`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // The Edge Functions gateway requires a Bearer credential even for
          // public functions (`verify_jwt = false`). The anonymous key is the
          // appropriate credential for a registration submitted before login.
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey,
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password, // untuk Supabase Auth saja
          full_name: formData.full_name,
          nick_name: formData.nick_name,
          gender: formData.gender,
          dob: formData.dob,
          phone: formData.phone,
          address: formData.address,
          id_card_number: formData.id_card_number,
          role_id: formData.role_id,
          member_type: formData.member_type,
          photo: photoUrl,
          id_card: id_cardUrl,
          skills: (formData.skills || []).map(skill =>
                    typeof skill === "object" ? skill.id ?? skill.value : skill
                  ),
        }),
      }
    );

    const result = await res.json();

    if (!res.ok || !result.success) {
      return {
        success: false,
        message: result.message || "Pendaftaran gagal",
      };
    }

    return {
      success: true,
      message: "Pendaftaran berhasil! Menunggu approval admin.",
    };
  } catch (err) {
    console.error("Register error:", err);
    return {
      success: false,
      message: err.message || "Terjadi kesalahan sistem",
    };
  }
};

export const updateMember = async (id, formData) => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) throw new Error('User not found in localStorage');

    let photoUrl = null;
    let id_cardUrl = null;

    const { data: existingMember, error: fetchError } = await supabase
      .from("members")
      .select("photo, id_card")
      .eq("id", id)
      .single();

    if (fetchError) throw fetchError;

    // Upload foto baru jika ada
    if (formData.photo && typeof formData.photo !== "string") {
      photoUrl = await uploadFileAndGetUrl(formData.photo, formData.full_name, "photo", "uploads");
      
      if(photoUrl && existingMember?.photo) {
        await deleteFileFromBucket(existingMember.photo);
      }
    }

    // Upload KTP baru jika ada
    if (formData.id_card && typeof formData.id_card !== "string") {
      id_cardUrl = await uploadFileAndGetUrl(formData.id_card, formData.full_name, "id_card", "uploads");
      
      if(id_cardUrl && existingMember?.id_card) {
        await deleteFileFromBucket(existingMember.id_card);
      }
    }

    // Konversi status ke integer
    const isActive = parseInt(formData.status, 10);

    // Data utama member
    const updates = {
      full_name: formData.full_name,
      nick_name: formData.nick_name,
      gender: formData.gender,
      dob: formData.dob,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      id_card_number: formData.id_card_number,
      id_card: id_cardUrl ? id_cardUrl : formData.id_card,
      photo: photoUrl ? photoUrl : formData.photo,
      role_id: formData.role_id,
      updated_at: new Date().toISOString(),
      updated_by: user.id,
      is_active: isActive,
      inactive_reason: isActive === 0 ? formData.inactive_reason : null,
    };

    // Update data member
    const { error: memberError } = await supabase.from("members").update(updates).eq("id", id);
    if (memberError) throw memberError;

    // Hapus semua skill lama dulu
    const { error: deleteError } = await supabase.from("member_skills").delete().eq("member_id", id);
    if (deleteError) throw deleteError;

    // Insert skill baru jika ada
    if (formData.skills && formData.skills.length > 0) {
      const skillData = formData.skills.map((skill) => ({
        member_id: id,
        skill_id: typeof skill === "object" ? skill.id : skill,
      }));

      const { error: insertError } = await supabase.from("member_skills").insert(skillData);
      if (insertError) throw insertError;
    }

    // Kirim email jika status member nonaktif
    if (isActive === 0) {
      await sendNonActiveEmail({ email: formData.email, fullName: formData.full_name }, formData.inactive_reason, 'dinonaktifkan');
    }

    return { success: true };
  } catch (err) {
    console.error("Update Error:", err);
    return { success: false, message: err.message };
  }
};

export const getMemberById = async (id) => {
  const { data, error } = await supabase
    .from('members')
    .select('id, full_name, nick_name, email, phone, gender, dob, address, id_card_number, id_card, photo, is_active, role_id')
    .eq('id', id)
    .single();

  if (error) {
    console.error('getMemberById error:', error.message);
    return null;
  }

  return data;
};

export const getMembers = async (search = '', limit = 10, offset = 0) => {
  let query = supabase
    .from('members')
    .select('*', { count: 'exact' })
    .not('member_id', 'is', null)
    .order('is_active', { ascending: false })
    .order('full_name', { ascending: true });

  if (search) {
    query = query.or(`full_name.ilike.%${search}%,member_id.ilike.%${search}%`);
  }

  if (limit !== "All") {
    query = query.range(offset, offset + limit - 1);
  }

  const { data, count, error } = await query;

  if (error) throw error;
  return { data, count };
};

export const blockirMember = async (id, blockir_reason) => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) throw new Error('User not found in localStorage');

    const { data: memberData, error: updateError } = await supabase
      .from('members')
      .update({ 
        blockir_reason: blockir_reason, 
        is_active: 0, 
        updated_at: new Date().toISOString(), 
        updated_by: user.id 
      })
      .eq('id', id)
      .select('full_name, email')
      .single();

    if (updateError) throw updateError;

    await sendNonActiveEmail({ email: memberData.email, fullName: memberData.full_name }, blockir_reason, 'diblokir');

    return { success: true };
  } catch (error) {
    console.error('Error blockir member:', error);
    return { success: false, error };
  }
};

export const unblockirMember = async (id) => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) throw new Error('User not found in localStorage');

    const { error: updateError } = await supabase
      .from('members')
      .update({
        is_active: 1,
        blockir_reason: null,
        updated_at: new Date().toISOString(),
        updated_by: user.id
      })
      .eq('id', id);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error) {
    console.error('Error unblocking member:', error);
    return { success: false, error };
  }
};

export const getMembersBySkills = async (skillIds = [], search = '', limit = 10, offset = 0) => {
  // Ambil member_id dari member_skills sesuai skillIds
  const { data: memberSkillData, error: skillError } = await supabase
    .from('member_skills')
    .select('member_id')
    .in('skill_id', skillIds);

  if (skillError) throw skillError;

  const memberIds = [...new Set(memberSkillData.map(ms => ms.member_id))]; // hapus duplikat

  // Ambil member detail
  let query = supabase
    .from('members')
    .select('*, member_skills(skill_id)', { count: 'exact' })
    .in('id', memberIds);

  if (search) {
    query = query.ilike('full_name', `%${search}%`);
  }

  if (limit !== 'All') {
    query = query.range(offset, offset + limit - 1);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  return { data, count };
};

export const getMemberSkills = async (member_id) => {
  const { data, error } = await supabase
    .from("member_skills")
    .select(`
      skill_id,
      skills ( id, name )
    `)
    .eq("member_id", member_id);

  if (error) {
    console.error("Error getMemberSkills:", error.message);
    throw error;
  }

  console.log("getMemberSkills result:", data); // ✅ cek hasilnya
  return { data };
};
